import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Loader, Notice } from '@origo/origo-ui';

interface LoadMoreTriggerProps {
    hasMore: boolean;
    isLoading: boolean;
    /** Forrige forsøk på å hente neste side feilet → ingen auto-lasting, vis «Prøv igjen». */
    isError?: boolean;
    onLoadMore: () => void;
    className?: string;
}

/** Hvor langt før bunnen av lista neste side begynner å lastes. */
const PREFETCH_MARGIN = '400px';

/**
 * Nærmeste forelder som scroller vertikalt. rootMargin virker bare på
 * observerens root, så den må være scroll-containeren og ikke viewporten.
 */
function scrollParent(el: HTMLElement): HTMLElement | null {
    for (let p = el.parentElement; p; p = p.parentElement) {
        const { overflowY } = getComputedStyle(p);
        if (overflowY === 'auto' || overflowY === 'scroll') return p;
    }
    return null;
}

/**
 * Bunnen av en lazy-loaded liste: laster neste side automatisk når den
 * nærmer seg synlig område. Knappen er fallback for tastatur og nettlesere
 * uten IntersectionObserver.
 */
const LoadMoreTrigger: React.FC<LoadMoreTriggerProps> = ({ hasMore, isLoading, isError = false, onLoadMore, className }) => {
    const { t } = useTranslation();
    const ref = useRef<HTMLDivElement>(null);
    const autoLoad = hasMore && !isError;

    // Observeren lages på nytt når onLoadMore endres (f.eks. etter at en side
    // er lastet). Er triggeren fortsatt innenfor marginen da, fyrer den straks
    // igjen — så korte sider fyller skjermen uten at brukeren må scrolle.
    // Etter en feil er auto-lasting av, ellers ville dette gitt en evig løkke.
    useEffect(() => {
        const el = ref.current;
        if (!el || !autoLoad || typeof IntersectionObserver === 'undefined') return;
        const observer = new IntersectionObserver(
            entries => {
                if (entries.some(e => e.isIntersecting)) onLoadMore();
            },
            { root: scrollParent(el), rootMargin: PREFETCH_MARGIN }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [autoLoad, onLoadMore]);

    if (!hasMore && !isLoading) return null;

    return (
        <div ref={ref} className={className}>
            {isLoading ? (
                <Loader size="sm" label={t('landing.loadingMore')} />
            ) : isError ? (
                <Notice variant="danger">
                    {t('landing.loadMoreError')}{' '}
                    <Button variant="secondary" size="sm" onClick={onLoadMore}>
                        {t('common.retry')}
                    </Button>
                </Notice>
            ) : (
                <Button variant="secondary" size="sm" onClick={onLoadMore}>
                    {t('landing.loadMore')}
                </Button>
            )}
        </div>
    );
};

export default LoadMoreTrigger;
